// Source-language detector for code blocks. Backed by `flourite`
// (teknologi-umum) — a small, dependency-free language classifier that
// scores a snippet against per-language signal patterns. We map its
// linguistic names onto our wire ids and cache results so the
// serializer can call this per keystroke cheaply.
//
// Detection only SEEDS the default; the user can always override via the
// toolbar's language selector.

import flourite from 'flourite';

export interface DetectableLanguage {
  /** Canonical id used on the wire (``` fence label). */
  readonly id: string;
  /** Human label for the language selector. */
  readonly label: string;
}

// The selectable catalog (also drives the toolbar dropdown). Ordered
// roughly by popularity. `plaintext` is the explicit "no language".
export const CODE_LANGUAGES: ReadonlyArray<DetectableLanguage> = [
  { id: 'plaintext', label: 'Plain Text' },
  { id: 'javascript', label: 'JavaScript' },
  { id: 'typescript', label: 'TypeScript' },
  { id: 'jsx', label: 'JSX / TSX' },
  { id: 'python', label: 'Python' },
  { id: 'json', label: 'JSON' },
  { id: 'html', label: 'HTML' },
  { id: 'css', label: 'CSS' },
  { id: 'bash', label: 'Shell / Bash' },
  { id: 'sql', label: 'SQL' },
  { id: 'java', label: 'Java' },
  { id: 'kotlin', label: 'Kotlin' },
  { id: 'swift', label: 'Swift' },
  { id: 'c', label: 'C' },
  { id: 'cpp', label: 'C++' },
  { id: 'csharp', label: 'C#' },
  { id: 'go', label: 'Go' },
  { id: 'rust', label: 'Rust' },
  { id: 'ruby', label: 'Ruby' },
  { id: 'php', label: 'PHP' },
  { id: 'yaml', label: 'YAML' },
  { id: 'markdown', label: 'Markdown' },
  { id: 'diff', label: 'Diff' },
];

const LABEL_BY_ID = new Map(CODE_LANGUAGES.map((l) => [l.id, l.label]));

/** Resolve a language id to its human label (falls back to the id). */
export function languageLabel(id: string | null | undefined): string {
  if (id === null || id === undefined || id.length === 0) return 'Plain Text';
  return LABEL_BY_ID.get(id) ?? id;
}

const FLOURITE_NAME_TO_ID: Readonly<Record<string, string>> = {
  javascript: 'javascript',
  typescript: 'typescript',
  python: 'python',
  java: 'java',
  'c++': 'cpp',
  'c#': 'csharp',
  c: 'c',
  go: 'go',
  golang: 'go',
  rust: 'rust',
  ruby: 'ruby',
  php: 'php',
  html: 'html',
  css: 'css',
  json: 'json',
  sql: 'sql',
  yaml: 'yaml',
  markdown: 'markdown',
  kotlin: 'kotlin',
  swift: 'swift',
  bash: 'bash',
  shell: 'bash',
  lua: 'lua',
  dart: 'dart',
  pascal: 'pascal',
  clojure: 'clojure',
  dockerfile: 'dockerfile',
};

// Small result cache (the serializer can call this on every keystroke).
// Keyed by the truncated snippet; bounded so it can't grow unbounded.
const detectCache = new Map<string, string | null>();
const DETECT_CACHE_MAX = 64;
// Cap the snippet length we feed the classifier so a huge paste can't
// make per-keystroke detection expensive (the head is plenty to判定).
const DETECT_INPUT_CAP = 4000;

// Flourite is signal-poor on the JS/TS/JSX family — a JSX snippet (lots of
// `{`, `"`, `:` from props) is frequently misread as `json`, and TS/JS as
// `markup`/`plaintext`. This heuristic catches the unambiguous cases so the
// fence label is right without the user having to fix it. JSON has no
// functions, arrows, template literals, comments, JSX tags, or imports.
function heuristicLanguage(code: string): string | null {
  const hasJsx =
    /<[A-Z][A-Za-z0-9]*[\s/>]/.test(code) || // <Component …
    /className=|<\/[a-zA-Z]|\/>/.test(code); // className=, </tag>, self-close
  const hasTsTypes =
    /:\s*(string|number|boolean|void|any|unknown|never)\b/.test(code) ||
    /\binterface\s+[A-Za-z]/.test(code) ||
    /\btype\s+\w+\s*=/.test(code) ||
    /\b(readonly|implements|enum)\b/.test(code);
  const hasJs =
    /=>|\bfunction\b|\bconst\b|\blet\b|\bimport\b|\bexport\b|\brequire\(|`[^`]*\$\{/.test(
      code,
    ) ||
    /^\s*\/\//m.test(code); // // comments
  if (hasJsx) return 'jsx';
  if (hasTsTypes) return 'typescript';
  if (hasJs) return 'javascript';
  return null;
}

/**
 * Detect the language of a code snippet. Returns a wire id (e.g.
 * `"typescript"`) or `null` when the classifier is unsure ("Unknown").
 */
export function detectLanguage(code: string): string | null {
  const trimmed = code.trim();
  if (trimmed.length === 0) return null;
  const key = trimmed.length > DETECT_INPUT_CAP ? trimmed.slice(0, DETECT_INPUT_CAP) : trimmed;
  const cached = detectCache.get(key);
  if (cached !== undefined) return cached;

  let id: string | null = null;
  try {
    const { language } = flourite(key, { noUnknown: false });
    if (typeof language === 'string' && language.length > 0 && language !== 'Unknown') {
      const norm = language.toLowerCase();
      id = FLOURITE_NAME_TO_ID[norm] ?? norm.replace(/[^a-z0-9+#]/g, '');
      if (id.length === 0) id = null;
    }
  } catch {
    id = null;
  }

  // Correct flourite's common web-language misfires with the heuristic.
  if (id === null || id === 'json' || id === 'markup' || id === 'plaintext' || id === 'markdown') {
    const heur = heuristicLanguage(key);
    if (heur !== null) id = heur;
  }

  if (detectCache.size >= DETECT_CACHE_MAX) {
    const first = detectCache.keys().next().value;
    if (first !== undefined) detectCache.delete(first);
  }
  detectCache.set(key, id);
  return id;
}
